import { ClippingStudioTextEvent, TextAlignment } from "./clipping-studio-project";

/**
 * Text overlay layout for the studio preview.
 *
 * The render draws captions through an HTML/canvas GFX template that owns the layout rules,
 * so this module reproduces those rules instead of inventing its own: glyphs are anchored by
 * their top edge, the plate grows outward from the measured text, and every style value in
 * pixels is scaled against the 540px reference minor axis.
 */

/** Minor-axis reference the studio and the render both scale pixel styling against. */
export const SIZE_REFERENCE_DIMENSION = 540;

/** CSS weights the render can reproduce (template exposes a bold flag only). */
export const REGULAR_FONT_WEIGHT = "400";
export const BOLD_FONT_WEIGHT = "700";

const BOLD_WEIGHT_THRESHOLD = 600;
const LINE_HEIGHT_FACTOR = 1.2;
const DEFAULT_FONT_SIZE = 18;
const DEFAULT_ANCHOR = 0.1;
const DEFAULT_ALIGNMENT: TextAlignment = "center";
const DEFAULT_BACKGROUND_PADDING = 12;
const DEFAULT_SHADOW_COLOR = "rgba(0,0,0,0.6)";
const DEFAULT_SHADOW_BLUR = 8;
const DEFAULT_SHADOW_OFFSET_X = 2;
const DEFAULT_SHADOW_OFFSET_Y = 2;
const MIN_NORMALIZED_SPAN = 0.001;
const GENERIC_FONT_FAMILIES = new Set([
  "sans-serif",
  "serif",
  "monospace",
  "cursive",
  "fantasy",
  "system-ui",
  "ui-sans-serif",
  "ui-serif",
  "ui-monospace",
]);

export interface TextLayoutInput {
  item: ClippingStudioTextEvent;
  /** Full overlay canvas size; for squeezed compositions this stays the pre-transform size. */
  canvasW: number;
  canvasH: number;
  /** CSS family already resolved to a registered face (or a generic fallback). */
  cssFamily: string;
  measureText: (fontSpec: string, text: string) => number;
}

export interface TextPlateLayout {
  color: string;
  left: number;
  top: number;
  width: number;
  height: number;
  radius: number;
}

export interface TextShadowLayout {
  color: string;
  blur: number;
  offsetX: number;
  offsetY: number;
}

export interface TextBounds {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface TextLayout {
  fontSpec: string;
  fontSizePx: number;
  /** Alignment passed straight to the canvas; the anchor already accounts for it. */
  textAlign: TextAlignment;
  /** fillText x, to be used with textBaseline = "top". */
  anchorX: number;
  /** fillText y, to be used with textBaseline = "top". */
  glyphTop: number;
  textWidthPx: number;
  /** Null when the item has no opaque background. */
  plate: TextPlateLayout | null;
  shadow: TextShadowLayout | null;
  /** Item rectangle including the decoration margin; used for hit-test and selection chrome. */
  bounds: TextBounds;
}

/** True when the item should be drawn bold, tolerating items that only carry a CSS weight. */
export function isBoldText(item: {
  bold?: boolean;
  fontWeight?: string;
}): boolean {
  if (item.bold !== undefined) {
    return item.bold;
  }
  return isBoldWeight(item.fontWeight);
}

/** True for CSS weights the render would draw bold. */
export function isBoldWeight(fontWeight: string | undefined | null): boolean {
  const parsed = Number.parseInt(fontWeight ?? "", 10);
  if (Number.isFinite(parsed)) {
    return parsed >= BOLD_WEIGHT_THRESHOLD;
  }
  const keyword = (fontWeight ?? "").trim().toLowerCase();
  return keyword === "bold" || keyword === "bolder";
}

/**
 * Collapses any incoming weight to the two values the render can reproduce and keeps the
 * `bold` flag in sync, so persisted projects never carry a weight the render would ignore.
 */
export function normalizeTextWeight(source: {
  fontWeight?: string;
  bold?: boolean;
}): { fontWeight: string; bold: boolean } {
  const bold = isBoldText(source);
  return {
    fontWeight: bold ? BOLD_FONT_WEIGHT : REGULAR_FONT_WEIGHT,
    bold,
  };
}

export function computeTextLayout(input: TextLayoutInput): TextLayout {
  const { item, canvasW, canvasH, cssFamily, measureText } = input;

  const sizeScale = Math.min(canvasW, canvasH) / SIZE_REFERENCE_DIMENSION;
  const fontSizePx = (item.fontSize ?? DEFAULT_FONT_SIZE) * sizeScale;
  const italic = item.italic ?? item.fontStyle === "italic";
  const fontSpec = buildFontSpec(fontSizePx, cssFamily, isBoldText(item), italic);

  const textWidthPx = Math.max(0, measureText(fontSpec, item.text ?? ""));
  const contentHeight = fontSizePx * LINE_HEIGHT_FACTOR;

  const backgroundColor = (item.backgroundColor ?? "").trim();
  const hasBackground = isVisibleBackground(backgroundColor);
  const paddingPx = hasBackground
    ? (item.backgroundPadding ?? DEFAULT_BACKGROUND_PADDING) * sizeScale
    : 0;
  const radiusPx = hasBackground
    ? (item.backgroundBorderRadius ?? 0) * sizeScale
    : 0;

  const shadow = item.dropShadow
    ? {
        color: item.shadowColor ?? DEFAULT_SHADOW_COLOR,
        blur: (item.shadowBlur ?? DEFAULT_SHADOW_BLUR) * sizeScale,
        offsetX: (item.shadowOffsetX ?? DEFAULT_SHADOW_OFFSET_X) * sizeScale,
        offsetY: (item.shadowOffsetY ?? DEFAULT_SHADOW_OFFSET_Y) * sizeScale,
      }
    : null;

  const marginPx =
    paddingPx +
    (shadow
      ? Math.ceil(
          shadow.blur + Math.max(Math.abs(shadow.offsetX), Math.abs(shadow.offsetY)),
        )
      : 0);

  const alignment = item.alignment ?? DEFAULT_ALIGNMENT;
  const anchorX = (item.x ?? DEFAULT_ANCHOR) * canvasW;
  const anchorY = (item.y ?? DEFAULT_ANCHOR) * canvasH;

  const rawContentLeft = anchorX - contentLeftOffset(alignment, textWidthPx);
  const rawContentTop = anchorY;

  const shift = resolveClampShift(
    rawContentLeft - marginPx,
    rawContentTop - marginPx,
    textWidthPx + marginPx * 2,
    contentHeight + marginPx * 2,
    canvasW,
    canvasH,
  );
  const contentLeft = rawContentLeft + shift.dx;
  const contentTop = rawContentTop + shift.dy;

  return {
    fontSpec,
    fontSizePx,
    textAlign: alignment,
    anchorX: contentLeft + textAnchorOffset(alignment, textWidthPx),
    glyphTop: contentTop,
    textWidthPx,
    plate: hasBackground
      ? buildPlate(
          backgroundColor,
          contentLeft,
          contentTop,
          textWidthPx,
          contentHeight,
          paddingPx,
          radiusPx,
        )
      : null,
    shadow,
    bounds: {
      left: contentLeft - marginPx,
      top: contentTop - marginPx,
      right: contentLeft + textWidthPx + marginPx,
      bottom: contentTop + contentHeight + marginPx,
    },
  };
}

function buildPlate(
  color: string,
  contentLeft: number,
  contentTop: number,
  textWidthPx: number,
  contentHeight: number,
  paddingPx: number,
  radiusPx: number,
): TextPlateLayout {
  const width = textWidthPx + paddingPx * 2;
  const height = contentHeight + paddingPx * 2;
  return {
    color,
    left: contentLeft - paddingPx,
    top: contentTop - paddingPx,
    width,
    height,
    radius: Math.max(0, Math.min(radiusPx, width / 2, height / 2)),
  };
}

/** Distance from the caption anchor to the left edge of the content box. */
function contentLeftOffset(alignment: TextAlignment, textWidthPx: number): number {
  if (alignment === "right") {
    return 0;
  }
  if (alignment === "left") {
    return textWidthPx;
  }
  return textWidthPx / 2;
}

/** Distance from the content box left edge to the point passed to fillText. */
function textAnchorOffset(alignment: TextAlignment, textWidthPx: number): number {
  if (alignment === "right") {
    return textWidthPx;
  }
  if (alignment === "left") {
    return 0;
  }
  return textWidthPx / 2;
}

/**
 * The render places the caption through a normalized rectangle that is clamped into the frame,
 * which shifts the whole drawing instead of resizing it. Oversized boxes are left overflowing.
 */
function resolveClampShift(
  left: number,
  top: number,
  width: number,
  height: number,
  canvasW: number,
  canvasH: number,
): { dx: number; dy: number } {
  if (canvasW <= 0 || canvasH <= 0) {
    return { dx: 0, dy: 0 };
  }

  const normWidth = clamp(width / canvasW, MIN_NORMALIZED_SPAN, 1);
  const normHeight = clamp(height / canvasH, MIN_NORMALIZED_SPAN, 1);
  const normLeft = clamp(left / canvasW, 0, 1 - normWidth);
  const normTop = clamp(top / canvasH, 0, 1 - normHeight);

  return {
    dx: normLeft * canvasW - left,
    dy: normTop * canvasH - top,
  };
}

function isVisibleBackground(trimmedColor: string): boolean {
  return trimmedColor.length > 0 && trimmedColor.toLowerCase() !== "transparent";
}

function buildFontSpec(
  fontSizePx: number,
  cssFamily: string,
  bold: boolean,
  italic: boolean,
): string {
  const style = italic ? "italic " : "";
  const weight = bold ? "bold " : "";
  return `${style}${weight}${fontSizePx}px ${quoteFamily(cssFamily)}`;
}

function quoteFamily(cssFamily: string): string {
  const family = (cssFamily ?? "").trim();
  if (family.length === 0) {
    return "sans-serif";
  }
  if (
    family.includes(",") ||
    family.startsWith('"') ||
    GENERIC_FONT_FAMILIES.has(family.toLowerCase())
  ) {
    return family;
  }
  return `"${family}"`;
}

function clamp(value: number, min: number, max: number): number {
  if (Number.isNaN(value)) {
    return min;
  }
  return Math.max(min, Math.min(max, value));
}
